import TomSelect from "tom-select";

function isTomSelectElement(input) {
  return !!input && typeof input.tagName === 'string' && typeof input.value === 'string';
}

function hasTomSelectValue(value) {
  return Array.isArray(value) ? value.length > 0 : value != null && value !== '';
}

function firstTomSelectValue(value) {
  return Array.isArray(value) ? value[0] : value;
}

function selectedTomSelectText(tomSelect) {
  const value = firstTomSelectValue(tomSelect.getValue());
  if (!hasTomSelectValue(value)) return '';

  const option = tomSelect.options?.[value];
  const labelField = tomSelect.settings?.labelField;
  const label = labelField ? option?.[labelField] : null;
  return typeof label === 'string' && label.length > 0 ? label : String(value);
}

function clearStaleTomSelectSelection(tomSelect, query) {
  const typedText = query?.trim();
  if (!typedText) return;

  const selectedText = selectedTomSelectText(tomSelect).trim();
  if (!selectedText || selectedText === typedText) return;

  tomSelect.clear(true);
  tomSelect.setTextboxValue(query);
  tomSelect.refreshOptions(false);
}

function syncCommittedTomSelectSelection(input, tomSelect) {
  const value = firstTomSelectValue(tomSelect.getValue());
  const selectedText = selectedTomSelectText(tomSelect).trim();

  if (hasTomSelectValue(value) && selectedText) {
    input.dataset.tomSelectCommittedValue = String(value);
    input.dataset.tomSelectCurrentQuery = selectedText;
    input.value = String(value);
  } else {
    delete input.dataset.tomSelectCommittedValue;
  }
}

function syncPendingTomSelectText(input) {
  const tomSelect = input?.tomselect;
  if (!tomSelect) return;

  const pendingText = tomSelect.control_input?.value?.trim();
  const storedQuery = input.dataset.tomSelectCurrentQuery?.trim();
  const committedValue = input.dataset.tomSelectCommittedValue?.trim();
  const selectedValue = tomSelect.getValue();

  if (pendingText) {
    input.value = pendingText;
    return;
  }

  if (committedValue) {
    input.value = committedValue;
    return;
  }

  if (storedQuery) {
    input.value = storedQuery;
    return;
  }

  if (!hasTomSelectValue(selectedValue)) {
    if (!input.value?.trim()) input.value = '';
  }
}

function finalizeSingleTomSelectSelection(tomSelect, value) {
  if (!hasTomSelectValue(value) || !tomSelect) return;

  requestAnimationFrame(() => {
    tomSelect.close(false);
    tomSelect.blur();
  });
}

function disableTomSelectRemoteQueryCache(tomSelect) {
  if (!tomSelect?.settings?.load) return;

  tomSelect.canLoad = function() {
    return true;
  };
}

function bindTomSelectSubmitSync(input) {
  const form = input.closest('form');
  if (!form || input.dataset.tomSelectSubmitSyncBound === 'true') return;

  form.addEventListener('submit', () => {
    syncPendingTomSelectText(input);
  }, { capture: true });

  input.dataset.tomSelectSubmitSyncBound = 'true';
}

function normalizeTomSelectClipboardValue(value) {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

function selectedTomSelectClipboardValue(input, tomSelect) {
  const committedValue = input?.dataset.tomSelectCommittedValue?.trim();
  if (committedValue) return committedValue;

  const selectedValue = firstTomSelectValue(tomSelect?.getValue());
  return hasTomSelectValue(selectedValue) ? String(selectedValue).trim() : '';
}

async function resolvePastedCardSelection(query, input) {
  const valueField = input?.tomselect?.settings?.valueField;
  const isCardId = valueField === 'id';
  const endpoint = isCardId ? '/autocomplete' : '/autocomplete_card';
  const response = await fetch(`${endpoint}?term=${encodeURIComponent(query)}`);
  if (!response.ok) return null;

  const items = await response.json();
  const normalizedQuery = normalizeTomSelectClipboardValue(query);

  if (isCardId) {
    return items.find(item => normalizeTomSelectClipboardValue(item?.id) === normalizedQuery) || null;
  }

  const match = items.find(item => normalizeTomSelectClipboardValue(item) === normalizedQuery);
  return match ? { value: match, label: match } : null;
}

function writeTomSelectClipboardText(value, focusTarget = null) {
  if (!value) return false;

  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.top = '-9999px';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  textarea.setSelectionRange(0, textarea.value.length);

  let copied = false;
  try {
    copied = document.execCommand('copy');
  } catch (_error) {
    copied = false;
  } finally {
    textarea.remove();
    focusTarget?.focus?.();
  }

  if (copied) return true;

  try {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(value);
      return true;
    }
  } catch (_error) {
    return false;
  }

  return false;
}

function sourceTomSelectInputFromControl(controlInput) {
  const wrapper = controlInput?.closest('.ts-wrapper');
  const field = wrapper?.parentElement;
  const localInput = field?.querySelector('input.ts-hidden-accessible, textarea.ts-hidden-accessible, select.ts-hidden-accessible');
  if (localInput?.tomselect) return localInput;

  const controlId = controlInput?.id;
  if (typeof controlId !== 'string' || !controlId.endsWith('-ts-control')) return null;

  const sourceInput = document.getElementById(controlId.replace(/-ts-control$/, ''));
  if (!sourceInput?.tomselect) return null;

  return sourceInput;
}

function tomSelectControlInputFromEvent(event) {
  const target = event?.target;
  if (target && typeof target.matches === 'function' && target.matches('.ts-control input[role="combobox"]')) {
    return target;
  }

  const path = typeof event?.composedPath === 'function' ? event.composedPath() : [];
  return path.find(node => {
    return node && typeof node.matches === 'function' && node.matches('.ts-control input[role="combobox"]');
  }) || null;
}

function tomSelectClipboardContextForEvent(event) {
  const controlInput = tomSelectControlInputFromEvent(event);
  if (!controlInput || typeof controlInput.matches !== 'function') return null;

  const input = sourceTomSelectInputFromControl(controlInput);
  const tomSelect = input?.tomselect;
  if (!input || !tomSelect) return null;

  return { input, tomSelect, controlInput };
}

async function applyTomSelectPastedText(input, tomSelect, pastedText) {
  pastedText = pastedText?.trim();
  if (!pastedText) return;

  input.dataset.tomSelectCurrentQuery = pastedText;
  tomSelect.clear(true);
  tomSelect.setTextboxValue(pastedText);

  const valueField = tomSelect.settings?.valueField || 'value';
  const labelField = tomSelect.settings?.labelField || 'label';

  if (!tomSelect.settings?.load) {
    const normalizedPaste = normalizeTomSelectClipboardValue(pastedText);
    const match = Object.values(tomSelect.options).find(opt => {
      return normalizeTomSelectClipboardValue(opt[valueField]) === normalizedPaste ||
             normalizeTomSelectClipboardValue(opt[labelField]) === normalizedPaste;
    });
    if (match) {
      tomSelect.setValue(match[valueField]);
    }
    return;
  }

  try {
    const match = await resolvePastedCardSelection(pastedText, input);
    if (match) {
      tomSelect.addOption(match);
      tomSelect.setValue(match[valueField]);
      return;
    }
  } catch (_error) {
    // Fall through to query-mode paste when exact resolution fails.
  }

  tomSelect.load(pastedText);
  tomSelect.refreshOptions(true);
}

function installTomSelectClipboardHandlers() {
  if (document.documentElement.dataset.tomSelectClipboardHandlersBound === 'true') return;

  document.addEventListener('keydown', event => {
    const context = tomSelectClipboardContextForEvent(event);
    if (!context) return;
    if ((event.ctrlKey || event.metaKey) !== true || event.altKey) return;
    const key = event.key.toLowerCase();
    const { input, tomSelect, controlInput } = context;

    if (key === 'c') {
      if (controlInput.value?.trim()) return;
      if (controlInput.selectionStart !== controlInput.selectionEnd) return;

      const selectedValue = selectedTomSelectClipboardValue(input, tomSelect);
      if (!selectedValue) return;

      event.preventDefault();
      writeTomSelectClipboardText(selectedValue, controlInput);
      return;
    }

    if (key !== 'v') return;
  }, true);

  document.addEventListener('copy', event => {
    const context = tomSelectClipboardContextForEvent(event);
    if (!context) return;

    const selectedValue = selectedTomSelectClipboardValue(context.input, context.tomSelect);
    if (!selectedValue || !event.clipboardData) return;

    event.preventDefault();
    event.clipboardData.setData('text/plain', selectedValue);
  }, true);

  document.addEventListener('paste', event => {
    const context = tomSelectClipboardContextForEvent(event);
    if (!context) return;

    const pastedText = event.clipboardData?.getData('text/plain')?.trim();
    if (!pastedText) return;

    event.preventDefault();
    const { input, tomSelect } = context;
    void applyTomSelectPastedText(input, tomSelect, pastedText);
  }, true);

  document.documentElement.dataset.tomSelectClipboardHandlersBound = 'true';
}

export function bindTomSelectClipboardSync(input) {
  if (!input?.tomselect || input.dataset.tomSelectClipboardSyncBound === 'true') return;
  installTomSelectClipboardHandlers();

  input.dataset.tomSelectClipboardSyncBound = 'true';
}

export function submitAutocompleteForm(form) {
  if (!form) return;

  if (typeof form.requestSubmit === 'function') {
    form.requestSubmit();
    return;
  }

  // Older browsers can still take the normal, cancelable submit-event path by
  // activating a temporary submitter. Do not retry with form.submit(): that
  // bypasses submit handlers and would submit even when one canceled the event.
  const submitter = document.createElement('button');
  submitter.type = 'submit';
  submitter.hidden = true;
  form.appendChild(submitter);
  try {
    submitter.click();
  } finally {
    submitter.remove();
  }
}

function submitTomSelectCurrentText(input) {
  syncPendingTomSelectText(input);
  submitAutocompleteForm(input.closest('form'));
}

function bindTomSelectQueryTracking(input) {
  const tomSelect = input?.tomselect;
  const controlInput = tomSelect?.control_input;
  if (!controlInput) return;
  if (input.dataset.tomSelectQueryTrackingBound === 'true' && input._tomSelectQueryTrackingControl === controlInput) return;

  const updateCurrentQuery = () => {
    const query = controlInput.value;
    if (query) {
      input.dataset.tomSelectCurrentQuery = query;
      if (input.dataset.tomSelectCommittedValue && input.dataset.tomSelectCommittedValue !== query) {
        delete input.dataset.tomSelectCommittedValue;
      }
    } else {
      delete input.dataset.tomSelectCurrentQuery;
    }
  };

  controlInput.addEventListener('input', () => {
    updateCurrentQuery();
  });

  controlInput.addEventListener('keyup', event => {
    if (event.key === 'Enter') return;
    updateCurrentQuery();
  });

  input._tomSelectQueryTrackingControl = controlInput;
  input.dataset.tomSelectQueryTrackingBound = 'true';
}

function bindTomSelectEnterSubmit(input) {
  const tomSelect = input?.tomselect;
  const controlInput = tomSelect?.control_input;
  if (!controlInput || input.dataset.tomSelectEnterSubmitBound === 'true') return;

  // `/q` uses TomSelect on a plain text search field. TomSelect can keep the
  // hidden input value, live textbox text, and selected option out of sync,
  // especially for prepopulated values and fast Enter submits. We track the
  // in-progress query separately and intercept Enter in capture phase so typed
  // text wins when nothing was selected, while committed selections still win
  // on normal submit.
  bindTomSelectQueryTracking(input);

  controlInput.addEventListener('keydown', event => {
    if (event.key !== 'Enter' || event.isComposing) return;

    const typedText = (controlInput.value || input.dataset.tomSelectCurrentQuery || '').trim();
    const selectedText = selectedTomSelectText(tomSelect).trim();

    if (!typedText || typedText === selectedText) return;

    event.preventDefault();
    event.stopPropagation();
    input.value = typedText;
    tomSelect.close();
    submitTomSelectCurrentText(input);
  }, true);

  input.dataset.tomSelectEnterSubmitBound = 'true';
}

function renderCardItem(item, escape) {
  let left = `<div class="search-card-name">${escape(item.name)}`;
  if (item.card_num) left += `<span class="card-num">#${escape(String(item.card_num))}</span>`;
  let hasFoilTag = false;
  if (item.tags?.length) {
    item.tags.forEach(tag => {
      if (tag.toLowerCase().includes('foil')) hasFoilTag = true;
      left += `<span class="badge badge-variation">${escape(tag)}</span>`;
    });
  }
  if (!hasFoilTag) {
    if (item.finish === 'foil') left += `<span class="badge badge-variation">Foil</span>`;
    else if (item.finish === 'foil_etched') left += `<span class="badge badge-variation">Foil Etched</span>`;
  }
  left += `</div>`;
  const setCode = (item.set_code || '').toLowerCase();
  const unsupported = window.__keyruneUnsupportedSvgMap || {};
  let setIcon;
  if (unsupported[setCode]) {
    setIcon = `<span class="set-symbol set-symbol-svg" style="mask-image: url(${escape(unsupported[setCode])}); -webkit-mask-image: url(${escape(unsupported[setCode])});" title="${escape(item.set || '')}"></span>`;
  } else {
    setIcon = `<i class="ss ss-${escape(setCode)}" title="${escape(item.set || '')}"></i>`;
  }
  const right = `<span class="search-set-name">${setIcon} ${escape(item.set_code || '')}</span>`;
  return `<div class="autocomplete-card-option">${left}${right}</div>`;
}

function buildRemoteCardAutocompleteItem(item) {
  const searchParts = [
    item?.name,
    item?.oracle_name,
    item?.id,
    item?.set,
    item?.set_code,
    item?.card_num,
    item?.finish,
    item?.finish_label,
    ...(Array.isArray(item?.tags) ? item.tags : []),
  ];

  return {
    ...item,
    search_text: buildAutocompleteSearchText(searchParts),
  };
}

function normalizeCardAutocompleteTerm(value) {
  return String(value).replace(/[.'\u2018\u2019]/g, '');
}

function buildAutocompleteSearchText(parts) {
  return parts.filter(Boolean).flatMap(part => {
    const text = String(part);
    const normalized = normalizeCardAutocompleteTerm(text);
    return normalized === text ? [text] : [text, normalized];
  }).join(' ');
}

function renderManaSymbols(colors, escape) {
  const colorString = typeof colors === 'string' ? colors : '';
  const symbols = colorString.toUpperCase().match(/[WUBRG]/g);
  if (!symbols?.length) return '';

  const labels = { W: 'White', U: 'Blue', B: 'Black', R: 'Red', G: 'Green' };
  const symbolHtml = symbols.map(symbol => (
    `<i class="ms ms-${escape(symbol.toLowerCase())} ms-cost ms-shadow" title="${escape(labels[symbol] || symbol)}"></i>`
  )).join('');
  return `<span class="manacost" aria-label="colors: ${escape(symbols.map(symbol => labels[symbol] || symbol).join(', '))}">${symbolHtml}</span>`;
}

function renderDeckItem(item, escape) {
  const format = item.format ? `<span class="search-result-format">${escape(item.format)}</span>` : '';
  const manaCost = item.mana_cost_html || renderManaSymbols(item.colors, escape);
  return `<div class="autocomplete-card-option autocomplete-deck-option"><span class="search-card-name">${escape(item.label || item.value)}${manaCost}</span>${format}</div>`;
}

function renderArticleItem(item, escape) {
  const publishedDate = item.published_date || item.date;
  const date = publishedDate ? `<span class="search-article-date">${escape(publishedDate)}</span>` : '';
  return `<div class="autocomplete-card-option autocomplete-article-option"><span class="search-card-name">${escape(item.label || item.value)}</span>${date}</div>`;
}

function renderTournamentItem(item, escape) {
  const meta = [item.date, item.format].filter(Boolean).map(value => escape(value)).join(' · ');
  const metaHtml = meta ? `<span class="search-tournament-meta">${meta}</span>` : '';
  return `<div class="autocomplete-card-option autocomplete-tournament-option"><span class="search-card-name">${escape(item.label || item.value)}</span>${metaHtml}</div>`;
}

function initRemoteSearchAutocomplete(input, { endpoint, submitOnSelect = false, renderOption = null } = {}) {
  if (!isTomSelectElement(input)) return null;
  if (input.tomselect) input.tomselect.destroy();

  const renderers = renderOption ? { option: renderOption, item: renderOption } : {};
  const tomSelect = new TomSelect(input, {
    valueField: 'value',
    labelField: 'label',
    searchField: ['search_text'],
    maxItems: 1,
    create: false,
    delimiter: '\x00',
    dropdownParent: 'body',
    shouldLoad: query => query.length >= 1,
    sortField: [{ field: 'search_order', direction: 'asc' }, { field: '$score' }],
    load(query, callback) {
      fetch(`${endpoint}?term=${encodeURIComponent(query)}`)
        .then(r => r.json())
        .then(items => callback(items.map((item, index) => ({
          ...item,
          search_order: index + 1,
          search_text: buildAutocompleteSearchText([item.label, item.value, item.format]),
        }))))
        .catch(() => callback());
    },
    render: renderers,
    onType(query) {
      clearStaleTomSelectSelection(this, query);
    },
    onChange(value) {
      syncCommittedTomSelectSelection(input, this);
      finalizeSingleTomSelectSelection(this, value);
      if (submitOnSelect && value) {
        const selectedOption = this.options?.[value];
        if (selectedOption?.url) {
          window.location.assign(selectedOption.url);
        } else {
          submitAutocompleteForm(input.closest('form'));
        }
      }
    }
  });
  disableTomSelectRemoteQueryCache(tomSelect);
  bindTomSelectSubmitSync(input);
  bindTomSelectClipboardSync(input);
  bindTomSelectQueryTracking(input);
  if (input.classList.contains('layout-search-input')) {
    tomSelect.dropdown.classList.add('navbar-search-dropdown');
    tomSelect.dropdown.dataset.bsTheme = 'dark';
  }
  if (input.classList.contains('autocomplete-submit-current')) {
    bindTomSelectEnterSubmit(input);
  }
  return tomSelect;
}

// Autocomplete for /autocomplete — rich card objects {name, id, card_uuid, set, tags, finish, card_num}
export function initCardIdAutocomplete(input, { submitOnSelect = false } = {}) {
  if (!isTomSelectElement(input)) return null;
  if (input.tomselect) input.tomselect.destroy();

  const preloadValue = input.value.trim();

  const tomSelect = new TomSelect(input, {
    valueField: 'id',
    labelField: 'name',
    searchField: ['search_text'],
    maxItems: 1,
    create: false,
    delimiter: '\x00',
    dropdownParent: 'body',
    shouldLoad: query => query.length >= 1,
    load(query, callback) {
      fetch(`/autocomplete?term=${encodeURIComponent(query)}`)
        .then(r => r.json())
        .then(items => callback(items.map(buildRemoteCardAutocompleteItem)))
        .catch(() => callback());
    },
    render: {
      option: renderCardItem,
      item: renderCardItem,
    },
    onType(query) {
      clearStaleTomSelectSelection(this, query);
    },
    onChange(value) {
      syncCommittedTomSelectSelection(input, this);
      finalizeSingleTomSelectSelection(this, value);
      if (submitOnSelect && value) {
        const selectedOption = this.options?.[value];
        if (selectedOption?.url) {
          window.location.assign(selectedOption.url);
        } else {
          submitAutocompleteForm(input.closest('form'));
        }
      }
    }
  });
  disableTomSelectRemoteQueryCache(tomSelect);
  bindTomSelectSubmitSync(input);
  bindTomSelectClipboardSync(input);
  bindTomSelectQueryTracking(input);
  if (input.classList.contains('layout-search-input')) {
    tomSelect.dropdown.classList.add('navbar-search-dropdown');
    tomSelect.dropdown.dataset.bsTheme = 'dark';
  }

  // Resolve preloaded value to full option data so the custom renderer fires.
  if (preloadValue) {
    resolvePastedCardSelection(preloadValue, input)
      .then(match => {
        if (match) {
          tomSelect.updateOption(preloadValue, match);
          tomSelect.setValue(match.id, true);
        }
      })
      .catch(() => {});
  }

  return tomSelect;
}

// Autocomplete for /autocomplete_card — simple string array of display names
export function initSimpleCardAutocomplete(input, { submitOnSelect = false, directCardNavigation = false } = {}) {
  if (!isTomSelectElement(input)) return null;
  if (input.tomselect) input.tomselect.destroy();
  const tomSelect = new TomSelect(input, {
    valueField: 'value',
    labelField: 'label',
    searchField: ['search_text'],
    maxItems: 1,
    create: false,
    delimiter: '\x00',
    dropdownParent: 'body',
    shouldLoad: query => query.length >= 1,
    load(query, callback) {
      const navigationParam = directCardNavigation ? '&direct_card_navigation=1' : '';
      fetch(`/autocomplete_card?term=${encodeURIComponent(query)}${navigationParam}`)
        .then(r => r.json())
        .then(items => callback(items.map(item => {
          const option = typeof item === 'string' ? { value: item, label: item } : item;
          return {
            ...option,
            search_text: buildAutocompleteSearchText([option.label, option.value]),
          };
        })))
        .catch(() => callback());
    },
    onType(query) {
      clearStaleTomSelectSelection(this, query);
    },
    onChange(value) {
      syncCommittedTomSelectSelection(input, this);
      finalizeSingleTomSelectSelection(this, value);
      if (submitOnSelect && value) {
        const selectedOption = this.options?.[value];
        if (selectedOption?.url) {
          window.location.assign(selectedOption.url);
        } else {
          submitAutocompleteForm(input.closest('form'));
        }
      }
    }
  });
  disableTomSelectRemoteQueryCache(tomSelect);
  bindTomSelectSubmitSync(input);
  bindTomSelectClipboardSync(input);
  bindTomSelectQueryTracking(input);
  if (input.classList.contains('autocomplete-submit-current')) {
    bindTomSelectEnterSubmit(input);
  }
  return tomSelect;
}

export function initDeckAutocomplete(input, { submitOnSelect = false } = {}) {
  return initRemoteSearchAutocomplete(input, {
    endpoint: '/autocomplete_decks',
    submitOnSelect,
    renderOption: renderDeckItem,
  });
}

export function initArticleAutocomplete(input, { submitOnSelect = false } = {}) {
  return initRemoteSearchAutocomplete(input, {
    endpoint: '/autocomplete_articles',
    submitOnSelect,
    renderOption: renderArticleItem,
  });
}

export function initTournamentAutocomplete(input, { submitOnSelect = false } = {}) {
  return initRemoteSearchAutocomplete(input, {
    endpoint: '/autocomplete_tournaments',
    submitOnSelect,
    renderOption: renderTournamentItem,
  });
}

// --- globals for legacy view callers ---

window.initCardIdAutocomplete = initCardIdAutocomplete;
window.initSimpleCardAutocomplete = initSimpleCardAutocomplete;
window.initDeckAutocomplete = initDeckAutocomplete;
window.initArticleAutocomplete = initArticleAutocomplete;
window.initTournamentAutocomplete = initTournamentAutocomplete;

export function bindAutoCompleteCard() {
  document.querySelectorAll('input.autocomplete-card, textarea.autocomplete-card, select.autocomplete-card').forEach(el => {
    if (el.closest('[data-controller~="nav-search-mode"]')) return;
    initSimpleCardAutocomplete(el);
  });
}
window.bindAutoCompleteCard = bindAutoCompleteCard;

export function bindSearchAutoComplete(elements, _searchBox, _searchButton, submit) {
  if (!elements) return;
  Array.from(elements).forEach(el => initCardIdAutocomplete(el, { submitOnSelect: !!submit }));
}
window.bindSearchAutoComplete = bindSearchAutoComplete;

function bindCardAutoComplete() {
  const isNavSearchInput = el => el.closest('[data-controller~="nav-search-mode"]');

  document.querySelectorAll('input.autocomplete-card-id, textarea.autocomplete-card-id, select.autocomplete-card-id').forEach(el => {
    if (isNavSearchInput(el)) return;
    initCardIdAutocomplete(el);
  });
  document.querySelectorAll('input.autocomplete-card-id-submit, textarea.autocomplete-card-id-submit, select.autocomplete-card-id-submit').forEach(el => {
    if (isNavSearchInput(el)) return;
    initCardIdAutocomplete(el, { submitOnSelect: true });
  });
}
window.bindCardAutoComplete = bindCardAutoComplete;

document.addEventListener('DOMContentLoaded', () => {
  bindCardAutoComplete();
  bindAutoCompleteCard();

  const loginBtn = document.getElementById('layout-login-signup-btn');
  if (loginBtn) {
    loginBtn.addEventListener('click', () => {
      document.getElementById('layout-login-signup-form')?.submit();
    });
  }
});
